/**
 * Booking Lead Time Chart Component
 *
 * Shows distribution of how far in advance customers book
 */

import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { BookingLeadTimeDistribution } from "../types/operations-types";
import { MetricTooltip } from "./metric-tooltip";

interface BookingLeadTimeChartProps {
  current: BookingLeadTimeDistribution[];
  previous: BookingLeadTimeDistribution[];
}

export function BookingLeadTimeChart({ current, previous }: BookingLeadTimeChartProps) {
  const { t } = useTranslation("reports");

  // Translate category helper
  const translateCategory = (category: string) => {
    const key = `charts.bookingLeadTime.categories.${category}` as const;
    return t(key, { defaultValue: category });
  };

  // Combine data for chart
  const chartData = current.map((curr, _index) => {
    const prev = previous.find((p) => p.category === curr.category);
    return {
      category: translateCategory(curr.category),
      originalCategory: curr.category,
      currentCount: curr.count,
      previousCount: prev?.count || 0,
      currentPercentage: curr.percentage,
      previousPercentage: prev?.percentage || 0,
    };
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center">
          {t("charts.bookingLeadTime.title")}
          <MetricTooltip content={t("charts.bookingLeadTime.tooltip")} />
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t("charts.bookingLeadTime.subtitle")}</p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Bar Chart */}
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="category"
                className="text-xs"
                tick={{ fontSize: 10 }}
                tickLine={false}
              />
              <YAxis className="text-xs" tick={{ fontSize: 10 }} tickLine={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "6px",
                  fontSize: "12px",
                }}
                labelStyle={{ fontWeight: 600 }}
                formatter={(value: number, name: string) => {
                  const label =
                    name === "currentCount" ? t("period.current") : t("period.previous");
                  return [value, label];
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: "12px" }}
                iconType="rect"
                formatter={(value) =>
                  value === "currentCount" ? t("period.current") : t("period.previous")
                }
              />
              <Bar dataKey="currentCount" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="previousCount" fill="#94a3b8" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>

          {/* Comparison Table */}
          <div className="space-y-2">
            {chartData.map((item) => {
              const countChange = item.currentCount - item.previousCount;
              const percentageChange =
                item.previousCount > 0
                  ? ((item.currentCount - item.previousCount) / item.previousCount) * 100
                  : 0;

              return (
                <div key={item.category} className="flex items-center gap-3 text-xs">
                  <div className="flex-1">
                    <span className="font-medium">{item.category}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <span>
                      {item.previousCount} → {item.currentCount}
                    </span>
                    {item.previousCount > 0 && (
                      <span
                        className={
                          countChange > 0 ? "text-green-600" : countChange < 0 ? "text-red-600" : ""
                        }
                      >
                        ({percentageChange > 0 ? "+" : ""}
                        {percentageChange.toFixed(0)}%)
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <span>{item.currentPercentage.toFixed(1)}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
